import React, { useContext, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import IconSvg from 'src/components/common/ui/IconSvg';
import { AuthContext } from 'src/contexts';
import PasswordStrengthIndicator from 'src/components/common/ui/PasswordStrengthIndicator';
import { useHttp, useNotification } from 'src/hooks';

const ManageAccount: React.FC = () => {
  const { sendRequest, error } = useHttp();
  const notify = useNotification();

  const { profile, accessToken, fetchProfile } = useContext(AuthContext);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    onSubmit: values => {
      const payload: any = {};

      if (values.firstName !== profile?.firstName) {
        payload.firstName = values.firstName;
      }

      if (values.lastName !== profile?.lastName) {
        payload.lastName = values.lastName;
      }

      if (values.newPassword) {
        payload.oldPassword = values.oldPassword;
        payload.newPassword = values.newPassword;
      }

      sendRequest(
        {
          method: 'PATCH',
          url: 'user/update-profile',
          accessToken,
          payload,
        },
        (data: any) => {
          notify(data.message, 'success');
          fetchProfile();
        },
      );
    },
    validate: values => {
      const errors: Record<string, string> = {};

      if (values.oldPassword || values.newPassword || values.confirmPassword) {
        if (!values.oldPassword) {
          errors.oldPassword = 'Old Password is required';
        }

        if (!values.newPassword) {
          errors.newPassword = 'New Password is required';
        } else {
          if (values.newPassword.length < 8) {
            errors.newPassword = 'Password must be at least 8 characters long.';
          } else if (!/(?=.*[a-z])/.test(values.newPassword)) {
            errors.newPassword = 'Password must contain at least one lowercase letter.';
          } else if (!/(?=.*[A-Z])/.test(values.newPassword)) {
            errors.newPassword = 'Password must contain at least one uppercase letter.';
          } else if (!/(?=.*[0-9])/.test(values.newPassword)) {
            errors.newPassword = 'Password must contain at least one number.';
          } else if (!/(?=.*[!@#$%^&*])/.test(values.newPassword)) {
            errors.newPassword = 'Password must contain at least one special character.';
          }
        }

        if (!values.confirmPassword) {
          errors.confirmPassword = 'Confirm Password is required';
        } else if (values.confirmPassword !== values.newPassword) {
          errors.confirmPassword = 'Passwords do not match';
        }
      }

      return errors;
    },
  });

  useEffect(() => {
    if (profile) {
      formik.setValues({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    }
  }, [profile]);

  const isNameChanged =
    formik.values.firstName !== profile?.firstName || formik.values.lastName !== profile?.lastName;

  const arePasswordFieldsFilledAndValid =
    formik.values.oldPassword &&
    formik.values.newPassword &&
    formik.values.confirmPassword &&
    !formik.errors.oldPassword &&
    !formik.errors.newPassword &&
    !formik.errors.confirmPassword;

  const isUpdateButtonEnabled = isNameChanged || arePasswordFieldsFilledAndValid;

  useEffect(() => {
    if (error) {
      notify(error || '', 'error');
    }
  }, [error]);

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-6 max-w-xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* First Name */}
        <div>
          <label htmlFor="firstName" className="block text-gray-700 text-sm font-medium mb-2">
            First name
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            className="w-full px-4 py-2 border rounded-lg text-sm"
          />
        </div>

        {/* Last Name */}
        <div>
          <label htmlFor="lastName" className="block text-gray-700 text-sm font-medium mb-2">
            Last name
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            className="w-full px-4 py-2 border rounded-lg text-sm"
          />
        </div>
      </div>

      {/* Email (Non-editable) */}
      <div>
        <label htmlFor="email" className="block text-gray-700 text-sm font-medium mb-2">
          Email
        </label>
        <input
          type="email"
          id="email"
          value={profile?.email}
          readOnly
          className="w-full px-4 py-2 border rounded-lg text-sm bg-gray-100 cursor-not-allowed"
        />
      </div>

      {/* Old Password */}
      <div>
        <label htmlFor="oldPassword" className="block text-gray-700 text-sm font-medium mb-2">
          Old Password
        </label>
        <div className="relative">
          <input
            type={showOldPassword ? 'text' : 'password'}
            id="oldPassword"
            name="oldPassword"
            value={formik.values.oldPassword}
            onChange={formik.handleChange}
            placeholder="Enter your old password"
            className="w-full px-4 py-2 border rounded-lg text-sm"
          />
          <span
            className="absolute inset-y-0 right-2.5 flex items-center cursor-pointer"
            onClick={() => setShowOldPassword(!showOldPassword)}
          >
            {showOldPassword ? (
              <IconSvg icon="hidePasswordEye" className="h-5 w-5" />
            ) : (
              <IconSvg icon="eye" className="h-5 w-5" />
            )}
          </span>
        </div>
        {formik.errors.oldPassword && (
          <p className="text-red-500 text-sm mt-1">{formik.errors.oldPassword}</p>
        )}
      </div>

      {/* New Password */}
      <div>
        <label className="flex items-center justify-between text-gray-700 text-sm font-medium mb-2">
          <span>New Password</span>
          <PasswordStrengthIndicator password={formik.values.newPassword} />
        </label>
        <div className="relative">
          <input
            type={showNewPassword ? 'text' : 'password'}
            id="newPassword"
            name="newPassword"
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            placeholder="Enter your new password"
            className="w-full px-4 py-2 border rounded-lg text-sm"
          />
          <span
            className="absolute inset-y-0 right-2.5 flex items-center cursor-pointer"
            onClick={() => setShowNewPassword(!showNewPassword)}
          >
            {showNewPassword ? (
              <IconSvg icon="hidePasswordEye" className="h-5 w-5" />
            ) : (
              <IconSvg icon="eye" className="h-5 w-5" />
            )}
          </span>
        </div>
        {formik.errors.newPassword && (
          <p className="text-red-500 text-sm mt-1">{formik.errors.newPassword}</p>
        )}
      </div>

      {/* Confirm New Password */}
      <div>
        <label htmlFor="confirmPassword" className="block text-gray-700 text-sm font-medium mb-2">
          Confirm New Password
        </label>
        <div className="relative">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            id="confirmPassword"
            name="confirmPassword"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            placeholder="Confirm your new password"
            className="w-full px-4 py-2 border rounded-lg text-sm"
          />
          <span
            className="absolute inset-y-0 right-2.5 flex items-center cursor-pointer"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            {showConfirmPassword ? (
              <IconSvg icon="hidePasswordEye" className="h-5 w-5" />
            ) : (
              <IconSvg icon="eye" className="h-5 w-5" />
            )}
          </span>
        </div>
        {formik.errors.confirmPassword && (
          <p className="text-red-500 text-sm mt-1">{formik.errors.confirmPassword}</p>
        )}
      </div>

      {/* Update Button */}
      <button
        type="submit"
        disabled={!isUpdateButtonEnabled}
        className={`w-full ${
          isUpdateButtonEnabled ? 'bg-blue-600' : 'bg-gray-400'
        } text-white py-2 rounded-md text-sm font-medium`}
      >
        Update
      </button>
    </form>
  );
};

export default ManageAccount;
